import { NavLink } from "react-router-dom";
import { Fragment, useState, type ReactNode } from "react";
import { FiLogOut } from "react-icons/fi";
import { useAuth } from "@/lib/auth";
import { logout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

export type SidebarItem = {
  to: string;
  label: string;
  icon: ReactNode;
  /** Section heading; a new heading renders wherever the group changes. */
  group?: string;
  /** Hidden from non-admins: the API answers 403 anyway, so a link would only mislead. */
  adminOnly?: boolean;
};

// Below md: off-canvas drawer (open/onClose). md+: static column; open is ignored.
export default function Sidebar({
  items,
  open,
  onClose,
}: {
  items: SidebarItem[];
  open: boolean;
  onClose: () => void;
}) {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const visible = items.filter((it) => !it.adminOnly || user?.role === "admin");
  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        id="app-nav"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-56 flex-col overflow-y-auto border-r border-rule bg-panel px-3 py-4 transition-transform",
          "md:static md:z-auto md:w-auto md:translate-x-0 md:bg-panel/60",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="eyebrow mb-4 px-3 text-slate-300 md:hidden">{t("app.name")}</div>
        <nav aria-label={t("nav.label")} className="flex flex-1 flex-col gap-0.5">
          {visible.map((it, i) => (
            <Fragment key={it.to}>
              {it.group && it.group !== visible[i - 1]?.group && (
                <div className="eyebrow mt-5 mb-1.5 px-3 text-slate-600">{it.group}</div>
              )}
              <NavLink
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "relative flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-colors md:min-h-8 md:py-1.5",
                    isActive
                      ? "bg-white/[0.04] text-slate-100 before:absolute before:inset-y-1.5 before:-left-3 before:w-[2px] before:rounded-r before:bg-signal"
                      : "text-slate-400 hover:bg-white/[0.03] hover:text-slate-100",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={cn("text-[15px]", isActive ? "text-signal" : "text-slate-500")}>{it.icon}</span>
                    {it.label}
                  </>
                )}
              </NavLink>
            </Fragment>
          ))}
        </nav>
        {user && (
          <div className="mt-4 flex items-center justify-between gap-2 border-t border-rule pt-3 md:hidden">
            <span className="truncate text-[11px] text-slate-400" title={user.email}>
              {user.email}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await logout();
                  setUser(null);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <FiLogOut /> {t("topbar.logout")}
            </Button>
          </div>
        )}
      </aside>
    </>
  );
}
